import Header from '../components/Header';
import Footer from '../components/Footer';
import { Mail, Phone, MapPin, Search, Send, CheckCircle2, MessageSquare } from 'lucide-react';
import { useState } from 'react';

export default function ContactUs() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Header */}
      <section className="pt-16 pb-32 bg-stripe-bg relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 text-center space-y-8 animate-in fade-in duration-700">
           <div className="inline-flex items-center gap-3 px-4 py-2 bg-primary-50 rounded-full border border-primary-100">
              <span className="text-xs font-bold text-primary-600 uppercase tracking-widest leading-none">Support & Sales</span>
           </div>
           
           <h1 className="text-5xl md:text-7xl font-black text-stripe-dark leading-[1.05] tracking-tight max-w-4xl mx-auto italic">
             Let's start a <span className="text-primary-500">conversation</span>.
           </h1>
           
           <p className="text-xl text-stripe-slate leading-relaxed max-w-2xl mx-auto font-medium">
             Have questions about our financing solutions? Our expert advisors are ready to help you choose the right path for your needs.
           </p>
        </div>
        
        {/* Subtle decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-50 rounded-full -translate-y-1/2 translate-x-1/2 -z-10 blur-3xl opacity-50" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-500/5 rounded-full translate-y-1/2 -translate-x-1/2 -z-10 blur-3xl opacity-50" />
      </section>

      {/* Main Content */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 -mt-32 relative z-20">
         <div className="grid lg:grid-cols-12 gap-12 lg:gap-24">
            
            {/* Contact Details */}
            <div className="lg:col-span-5 space-y-12">
               <div className="space-y-4 px-2">
                  <h3 className="text-2xl font-black text-stripe-dark uppercase tracking-tight italic">Direct Access</h3>
                  <p className="text-stripe-slate font-medium text-lg leading-relaxed">Reach out directly via our secure support channels for immediate assistance.</p>
               </div>
               
               <div className="space-y-6">
                  {[
                    { label: 'General Inquiry', value: 'support@finova.lk', icon: Mail, color: 'text-primary-500', bg: 'bg-primary-50' },
                    { label: 'Corporate Sales', value: 'sales@finova.lk', icon: MessageSquare, color: 'text-stripe-blue', bg: 'bg-stripe-bg' },
                    { label: 'Support Hotline', value: '+94 11 234 5678', icon: Phone, color: 'text-green-500', bg: 'bg-green-50' },
                    { label: 'Headquarters', value: '72nd Floors, World Trade Center, Colombo 01', icon: MapPin, color: 'text-stripe-dark', bg: 'bg-stripe-bg' }
                  ].map((c, i) => (
                    <div key={i} className="s-card hover:border-primary-500 transition-all group">
                       <div className="flex items-center gap-8">
                          <div className={`w-14 h-14 ${c.bg} rounded-2xl shrink-0 flex items-center justify-center ${c.color} shadow-sm group-hover:scale-110 transition-transform`}>
                             <c.icon size={26} />
                          </div>
                          <div className="space-y-1.5 text-left">
                             <p className="text-[10px] font-black text-stripe-slate uppercase tracking-widest">{c.label}</p>
                             <p className="text-lg font-black text-stripe-dark tracking-tight leading-none italic">{c.value}</p>
                          </div>
                       </div>
                    </div>
                  ))}
               </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-7">
               <div className="bg-white p-12 lg:p-16 rounded-[40px] shadow-2xl border border-stripe-border relative group">
                  {submitted ? (
                    <div className="text-center py-24 space-y-8 animate-in zoom-in duration-500">
                       <div className="w-24 h-24 bg-green-50 text-green-500 rounded-full mx-auto flex items-center justify-center shadow-lg border border-green-100">
                          <CheckCircle2 size={48} />
                       </div>
                       <div className="space-y-3">
                          <h3 className="text-3xl font-black text-stripe-dark tracking-tighter italic leading-none">Message Transmitted</h3>
                          <p className="text-stripe-slate text-lg font-medium">An advisor will respond within 2-4 business hours. Prepare for your future.</p>
                       </div>
                       <button onClick={() => setSubmitted(false)} className="btn-secondary px-8 font-black rounded-full text-xs tracking-widest uppercase">Send Another Message</button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-8">
                       <div className="space-y-2 translate-y-[-20px] px-2 text-left">
                          <h4 className="text-xs font-black text-primary-500 uppercase tracking-widest">Connect with us</h4>
                          <p className="text-2xl font-black text-stripe-dark tracking-tight">Tell us about your requirements.</p>
                       </div>
                       
                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="space-y-2 text-left">
                             <label className="s-label uppercase tracking-widest text-[10px] font-black">Full Identity</label>
                             <input type="text" className="s-input bg-stripe-bg border-none py-4 text-base font-bold italic" placeholder="e.g. John Doe" required />
                          </div>
                          <div className="space-y-2 text-left">
                             <label className="s-label uppercase tracking-widest text-[10px] font-black">Digital Reach</label>
                             <input type="email" className="s-input bg-stripe-bg border-none py-4 text-base font-bold italic" placeholder="john@company.com" required />
                          </div>
                       </div>
                       
                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="space-y-2 text-left">
                             <label className="s-label uppercase tracking-widest text-[10px] font-black">Company Signature</label>
                             <input type="text" className="s-input bg-stripe-bg border-none py-4 text-base font-bold italic" placeholder="Optional" />
                          </div>
                          <div className="space-y-2 text-left">
                             <label className="s-label uppercase tracking-widest text-[10px] font-black">Subject Area</label>
                             <select className="s-select bg-stripe-bg border-none py-4 text-base font-bold italic h-[56px] px-12">
                                <option>Quick Loan Assistance</option>
                                <option>SME/Business Financing</option>
                                <option>Security & Identity</option>
                                <option>Developer & API Partners</option>
                             </select>
                          </div>
                       </div>
                       
                       <div className="space-y-2 text-left">
                          <label className="s-label uppercase tracking-widest text-[10px] font-black">Detailed Message</label>
                          <textarea className="s-textarea bg-stripe-bg border-none py-4 h-48 text-base font-bold italic" placeholder="How can we help you expand today?" required></textarea>
                       </div>
                       
                       <button type="submit" className="btn-primary w-full py-5 rounded-full text-base font-black shadow-xl hover:shadow-primary-500/20 active:scale-95 flex items-center justify-center gap-3">
                         <Send size={20} />
                         Transmit Inquiry
                       </button>
                    </form>
                  )}
               </div>
            </div>
         </div>
      </section>

      <Footer />
    </div>
  );
}
